// src/components/search/SiteSearch.tsx
//
// Global site-search entry points: the ⌘K / Ctrl-K shortcut listener, the
// tiny navbar trigger button, and the lazy palette host.
//
// Performance contract (owner priority — zero JS added to initial page load):
// this module ships only a button + one keydown listener inside the existing
// Navbar client chunk. The actual palette (cmdk dialog + MiniSearch + corpus
// fetch) lives in `SiteSearchPalette`, dynamic-imported with `ssr: false` the
// FIRST time search is opened — until then no palette code, no corpus bytes,
// and no index work are loaded. After first open the palette stays mounted so
// the corpus/index are built once per session.

'use client'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import { ensureInputModalityTracking } from '~/lib/a11y/input-modality'
import { trackSiteAction } from '~/lib/gtag'
import { normalizeActionLabel, surfaceFromLocation } from '~/lib/site-intelligence/actions'
import { cx } from '~/lib/utils'

import { useSiteSearchShortcutHint } from './useSiteSearchShortcutHint'

const SiteSearchPalette = dynamic(() => import('./SiteSearchPalette'), { ssr: false })

/**
 * Window event any surface can dispatch to open the global search palette —
 * lets the navbar (and future hub-embedded affordances) trigger the palette
 * without threading state through the tree.
 */
export const OPEN_SITE_SEARCH_EVENT = 'adsights:open-site-search'

/**
 * Opens the global search palette (no-op during SSR). The analytics surface
 * is derived from `source` (e.g. `navbar-desktop` → `navbar`), so future
 * non-navbar affordances attribute correctly without touching this function.
 */
export function openSiteSearch(source: string): void {
  if (typeof window === 'undefined') return
  trackSiteAction({
    kind: 'toggle',
    label: normalizeActionLabel('Open Site Search'),
    surface: surfaceFromLocation(source),
    element_text: source,
  })
  window.dispatchEvent(new Event(OPEN_SITE_SEARCH_EVENT))
}

/**
 * Palette host + global shortcut owner. Mount exactly once (inside the
 * Navbar, which renders on every page).
 */
export function SiteSearch(): ReactNode {
  const [open, setOpen] = useState(false)
  // Never reset — keeps the lazy chunk + corpus/index mounted after first use.
  const [hasOpened, setHasOpened] = useState(false)

  const openPalette = useCallback((): void => {
    setOpen(true)
    setHasOpened(true)
  }, [])

  useEffect(() => {
    // Start tracking keyboard-vs-pointer modality from page load, before the
    // lazy palette mounts — the palette gates its focus ring on this, and the
    // interaction that opens it (a pointerdown / ⌘K) fires before the palette
    // (and thus its own listeners) would exist. See input-modality.ts.
    ensureInputModalityTracking()

    const onOpenEvent = (): void => openPalette()

    // ⌘K / Ctrl-K with the editable-element guard (pattern shared with the
    // blog search page's page-local listener): never steal the shortcut while
    // the user is typing in an input/textarea/select/contenteditable.
    const onKeyDown = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey) || typeof e.key !== 'string' || e.key.toLowerCase() !== 'k')
        return
      // A page-local ⌘K handler (e.g. /blog/search's input-focus listener)
      // may have claimed the shortcut already — the page-local claimant wins.
      // Deterministic, not registration-order luck: page-local listeners
      // register in the CAPTURE phase (see SearchInput.tsx), which always
      // precedes this bubble-phase listener regardless of mount order (hard
      // load vs SPA navigation).
      if (e.defaultPrevented) return
      const active = document.activeElement
      const editing =
        active instanceof HTMLElement &&
        (active.isContentEditable ||
          active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          active instanceof HTMLSelectElement)
      if (editing) return
      e.preventDefault()
      e.stopImmediatePropagation()
      // Same consent-gated open event the pointer surfaces send, so
      // keyboard-vs-button open attribution is measurable.
      trackSiteAction({
        kind: 'toggle',
        label: normalizeActionLabel('Open Site Search'),
        surface: 'global',
        element_text: 'keyboard-shortcut',
      })
      openPalette()
    }

    window.addEventListener(OPEN_SITE_SEARCH_EVENT, onOpenEvent)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener(OPEN_SITE_SEARCH_EVENT, onOpenEvent)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [openPalette])

  if (!hasOpened) return null
  return <SiteSearchPalette open={open} onOpenChange={setOpen} />
}

interface SiteSearchTriggerProps {
  className?: string
}

/**
 * Desktop navbar trigger: a compact search-icon button with a keyboard hint.
 * Renders the platform-appropriate hint after mount (SSR default: ⌘K).
 */
export function SiteSearchTrigger({ className }: SiteSearchTriggerProps): ReactNode {
  const shortcutHint = useSiteSearchShortcutHint()

  return (
    <button
      type="button"
      onClick={() => openSiteSearch('navbar-desktop')}
      aria-label="Search the site"
      aria-haspopup="dialog"
      aria-keyshortcuts="Meta+K Control+K"
      className={cx(
        'group flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white/60 px-3 text-gray-500 transition-colors',
        'hover:border-gray-300 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
        'dark:border-white/10 dark:bg-gray-900/60 dark:text-gray-400 dark:hover:border-white/20 dark:hover:text-gray-50',
        className,
      )}
    >
      <RiSearchLine aria-hidden="true" className="size-4" />
      {/* Keyboard hint appears at `xl` (not `lg`): at 1024–1279px its extra
          ~50px pushed the trigger into the absolutely centered nav (measured
          23px overlap at 1024px — see the Navbar layout note). */}
      <kbd
        aria-hidden="true"
        className="hidden rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-500 xl:inline-flex dark:bg-gray-800 dark:text-gray-400"
      >
        {shortcutHint}
      </kbd>
    </button>
  )
}
